import React, { useState, useContext, useEffect } from 'react';
import { Button, CurrencyInput, Section, Text } from '@prenuvo/shared-library';
import { fetchAppsResource } from 'packages/apis';
import StripeElements from './StripeElements';
import { PaymentContext } from '../PaymentContext';
import { StudyContext, BookingContext } from 'packages/booking-contexts';
import { loadStripe } from '@stripe/stripe-js';

const StripePayment = ({ transaction, setTransaction, openAlert, onChange }) => {
    const [showStripeForm, setShowStripeForm] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [paymentStatus, setPaymentStatus] = useState(null);
    const { total, paymentType, invoiceSummary, studyId, recordPayment, load } = useContext(PaymentContext);
    const { selectedLocation } = useContext(StudyContext);
    const { stripeTokens } = useContext(BookingContext);
    const publishableKey = stripeTokens?.[selectedLocation?.locationID];

    useEffect(() => {
        setShowStripeForm(false);
    }, [paymentType]);

    useEffect(() => {
        const balance = invoiceSummary.totalBalance || total;
        setTransaction(prev => ({
            ...prev,
            amount: isNaN(Number(balance)) ? 0 : Number(balance),
            locationID: selectedLocation?.locationID
        }));
    }, [total, invoiceSummary.totalBalance, selectedLocation]);

    // Handle Stripe redirect return (e.g. 3D Secure)
    useEffect(() => {
        const checkStripeReturn = async () => {
            const params = new URLSearchParams(window.location.search);
            const clientSecret = params.get('payment_intent_client_secret');
            if (clientSecret && publishableKey) {
                const stripe = await loadStripe(publishableKey);
                const { paymentIntent } = await stripe.retrievePaymentIntent(clientSecret);
                if (paymentIntent.status === 'succeeded') {
                    try {
                        await recordPayment({
                            paymentType: 'stripe',
                            amount: paymentIntent.amount / 100,
                            details: paymentIntent,
                        });
                    } catch (error) {
                        openAlert('Payment Error', 'Payment was processed by Stripe, but failed to record in our system. Please contact support or try again.');
                        return;
                    }
                }
                setPaymentStatus(paymentIntent.status);
                params.delete('payment_intent_client_secret');
                const newUrl = window.location.pathname + (params.toString() ? '?' + params.toString() : '');
                window.history.replaceState({}, '', newUrl);
            }
        };
        checkStripeReturn();
    }, [publishableKey]);

    const handleAmountSubmit = async () => {
        setIsLoading(true);
        try {
            const { parsedBody } = await fetchAppsResource(`/payment/study/${studyId}/payment-intent`, 'POST', {
                amount: transaction.amount
            });
            setTransaction(prev => ({
                ...prev,
                clientSecret: parsedBody.client_secret
            }));
            setShowStripeForm(true);
        } catch (error) {
            openAlert('Error', error.message || 'Failed to initialize payment');
        }
        setIsLoading(false);
    };

    // Call recordPayment after successful payment
    const handleStripeChange = async (status, timestamp, paymentIntent) => {
        if (status === 'succeeded' && paymentIntent) {
            try {
                await recordPayment({
                    paymentType: 'stripe',
                    amount: paymentIntent.amount / 100,
                    details: paymentIntent,
                });
                await load(); // Refresh transactions and invoice summary after successful payment
            } catch (error) {
                openAlert('Payment Error', 'Payment was processed by Stripe, but failed to record in our system. Please contact support or try again.');
                return;
            }
        } else if (status === 'requires_capture' && paymentIntent) {
            // Payment is authorized but needs capture - let the backend handle it
            try {
                await recordPayment({
                    paymentType: 'stripe',
                    amount: paymentIntent.amount / 100,
                    details: paymentIntent,
                });
                await load(); // Refresh transactions and invoice summary after successful payment
            } catch (error) {
                openAlert('Payment Error', 'Payment was authorized by Stripe, but failed to record in our system. Please contact support or try again.');
                return;
            }
        }
        setPaymentStatus(status);
        if (onChange) onChange(status, timestamp);
    };

    if (paymentStatus === 'succeeded' || paymentStatus === 'requires_capture') {
        return (
            <Section>
                <Text color="green">Payment successful! Thank you.</Text>
            </Section>
        );
    }
    if (paymentStatus === 'requires_capture') {
        return (
            <Section>
                <Text color="green">Payment authorized successfully! Processing capture...</Text>
            </Section>
        );
    }
    if (paymentStatus === 'processing') {
        return (
            <Section>
                <Text color="orange">Payment processing. Please wait...</Text>
            </Section>
        );
    }
    if (paymentStatus === 'requires_payment_method' || paymentStatus === 'canceled') {
        return (
            <Section>
                <Text color="red">Payment failed or canceled. Please try again.</Text>
            </Section>
        );
    }

    return (
        <>
            {!showStripeForm ? (
                <Section>
                    <CurrencyInput
                        label="Enter Payment Amount"
                        value={
                            isNaN(Number(transaction.amount)) || transaction.amount === null || transaction.amount === undefined
                                ? ""
                                : Number(transaction.amount)
                        }
                        onChange={(values) => {
                            setTransaction({
                                ...transaction,
                                amount: values.value
                            });
                        }}
                        fixedDecimalScale
                        decimalScale={2}
                        allowNegative={false}
                        placeholder="0.00"
                    />
                    <Button
                        variant="primary"
                        size="small"
                        onClick={handleAmountSubmit}
                        disabled={isLoading}
                    >
                        {isLoading ? 'Validating...' : 'Continue to Payment'}
                    </Button>
                </Section>
            ) : (
                <StripeElements
                    openAlert={openAlert}
                    transaction={transaction}
                    onChange={(status, timestamp, paymentIntent) => handleStripeChange(status, timestamp, paymentIntent)}
                />
            )}
        </>
    );
};

export default StripePayment; 